// mediaPermissions.ts
// Utility for handling camera and microphone permissions

import { logWithSuffix } from './clientLogger'

export interface MediaPermissionState {
  camera: PermissionState | 'unknown'
  microphone: PermissionState | 'unknown'
  hasPermissions: boolean
  error?: string
  lastChecked?: number
  deviceAvailability?: {
    hasCamera: boolean
    hasMicrophone: boolean
  }
}

export interface MediaStreamInfo {
  video: boolean
  audio: boolean
  stream?: MediaStream
  error?: string
}

/** Stop all tracks so Unblu/LiveKit can own the camera during calls. */
export function stopMediaStream(stream: MediaStream | null | undefined): void {
  if (!stream) return
  stream.getTracks().forEach((track) => track.stop())
}

export class MediaPermissionManager {
  private static instance: MediaPermissionManager
  private permissionState: MediaPermissionState = {
    camera: 'unknown',
    microphone: 'unknown',
    hasPermissions: false,
    lastChecked: Date.now()
  }
  private permissionListeners: Array<() => void> = []
  private isListening = false
  private previousDevices: MediaDeviceInfo[] = []
  private deviceChangeHandler: (() => void) | null = null
  private deviceChangeDebounceTimer: number | null = null
  private lastLogitechWarningTime: number = 0
  private readonly LOGITECH_WARNING_COOLDOWN_MS = 60000 // Only show warning once per minute
  private readonly DEVICE_CHANGE_DEBOUNCE_MS = 300 // Wait 300ms to batch multiple device change events
  private lastDeviceChangeLogTime: number = 0
  private lastDeviceChangeHash: string = ''
  private readonly DEVICE_CHANGE_LOG_COOLDOWN_MS = 1000 // Prevent duplicate logs for same change within 1 second
  private permissionRecheckTimer: number | null = null
  private readonly PERMISSION_RECHECK_DEBOUNCE_MS = 150

  private constructor() {
    // Load permission state asynchronously (fire and forget)
    this.loadPermissionState().catch(() => {
      // Silently fail - state will be checked on next permission check
    })
    this.setupDeviceChangeListener().catch(() => {
      // Silently fail - device change listener setup
    })
  }

  static getInstance(): MediaPermissionManager {
    if (!MediaPermissionManager.instance) {
      MediaPermissionManager.instance = new MediaPermissionManager()
    }
    return MediaPermissionManager.instance
  }

  /**
   * Check if a device label matches Logitech PTZ camera patterns
   * Detects various Logitech camera models:
   * - Logitech PTZ Pro
   * - Logitech PTZ Pro 2
   * - Logi Group Camera (Logitech Group system)
   * - Logitech Rally Camera
   * - Logitech MeetUp
   * - Rally Bar / Rally Bar Mini
   * - Other Logitech PTZ / conference variations
   */
  private isLogitechPTZCamera(label: string): boolean {
    if (!label || label === '(label unavailable - permissions required)') {
      return false
    }
    
    const lowerLabel = label.toLowerCase()

    // Brand/vendor hints
    const hasLogitech = lowerLabel.includes('logitech') || lowerLabel.includes('logi')
    const hasVendorId = lowerLabel.includes('(046d:') // Logitech USB vendor ID appears in labels

    // Model / family hints
    const hasPTZ = lowerLabel.includes('ptz') // PTZ Pro / PTZ Pro 2
    const hasGroup = lowerLabel.includes('group') // Logitech Group
    const hasRally = lowerLabel.includes('rally') // Rally, Rally Camera, Rally Bar/Mini
    const hasMeetup = lowerLabel.includes('meetup') // Logitech MeetUp
    const hasConference = lowerLabel.includes('conferencecam') // older conference cam labels

    // If we have either brand or vendor ID AND any known model keyword, consider it Logitech
    const brandOrVendor = hasLogitech || hasVendorId
    const modelMatch = hasPTZ || hasGroup || hasRally || hasMeetup || hasConference

    return brandOrVendor && modelMatch
  }

  /**
   * Check device availability
   * @param quietMode - If true, suppresses verbose logging (useful during device change events)
   */
  async checkDeviceAvailability(quietMode: boolean = false): Promise<{ hasCamera: boolean, hasMicrophone: boolean }> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      
      // Extract detailed device information
      const cameras = devices.filter(d => d.kind === 'videoinput').map(d => ({
        deviceId: d.deviceId,
        label: d.label || '(label unavailable - permissions required)',
        kind: d.kind,
        groupId: d.groupId
      }))
      
      const microphones = devices.filter(d => d.kind === 'audioinput').map(d => ({
        deviceId: d.deviceId,
        label: d.label || '(label unavailable - permissions required)',
        kind: d.kind,
        groupId: d.groupId
      }))
      
      const audioOutputs = devices.filter(d => d.kind === 'audiooutput').map(d => ({
        deviceId: d.deviceId,
        label: d.label || '(label unavailable - permissions required)',
        kind: d.kind,
        groupId: d.groupId
      }))
      
      const availability = {
        hasCamera: cameras.length > 0,
        hasMicrophone: microphones.length > 0
      }
      
      // Check for Logitech cameras (PTZ, Group, Rally, etc.)
      // ALWAYS check and warn if missing - this is critical and must be checked every minute
      // Even in quiet mode (periodic checks), we need to know if Logitech camera is missing
      const logitechCameras = cameras.filter(cam => this.isLogitechPTZCamera(cam.label))
      
      if (logitechCameras.length === 0 && cameras.length > 0) {
        // Only warn if we have camera labels (permissions granted) and no Logitech camera found
        const hasLabels = cameras.some(cam => cam.label && cam.label !== '(label unavailable - permissions required)')
        if (hasLabels) {
          // Always warn if Logitech camera is missing (throttled to once per minute to prevent spam)
          const now = Date.now()
          if (now - this.lastLogitechWarningTime > this.LOGITECH_WARNING_COOLDOWN_MS) {
            logWithSuffix('warn', 'NO-LOGITECH-CAM-FOUND', 'Warning: No Logitech Camera found (PTZ Pro, PTZ Pro 2, Group, Rally, etc.). Available cameras:', cameras.map(c => c.label).filter(Boolean))
            this.lastLogitechWarningTime = now
          }
        }
      } else if (logitechCameras.length > 0 && !quietMode) {
        // Only log "found" message in non-quiet mode (not needed for periodic checks)
        logWithSuffix('debug', 'LOGITECH-CAM-FOUND', 'Logitech Camera(s) found:', logitechCameras.map(c => c.label))
      }
      
      // Log detailed device information in non-quiet mode
      // Always show device lists and counts on initial load and manual checks (not during periodic quiet checks)
      if (!quietMode) {
        logWithSuffix('debug', 'DEVICE-AVAILABILITY', 'Device availability:', availability)
        logWithSuffix('debug', 'DEVICE-LIST-CAM', 'Camera devices:', cameras.length > 0 ? cameras : 'none')
        logWithSuffix('debug', 'DEVICE-LIST-AUDIO', 'Microphone devices:', microphones.length > 0 ? microphones : 'none')
        logWithSuffix('debug', 'DEVICE-LIST-OUTPUT', 'Audio output devices:', audioOutputs.length > 0 ? audioOutputs : 'none')
        logWithSuffix('debug', 'DEVICE-COUNTS', 'Device counts:', {
          cameras: cameras.length,
          microphones: microphones.length,
          audioOutputs: audioOutputs.length,
          total: cameras.length + microphones.length + audioOutputs.length
        })
      }
      
      // Detect device changes
      this.detectDeviceChanges(devices)
      
      // Store current devices for next comparison
      this.previousDevices = devices
      
      return availability
    } catch (error) {
      logWithSuffix('warn', 'DEVICE-ENUM-ERROR', 'Could not enumerate devices:', error)
      return { hasCamera: false, hasMicrophone: false }
    }
  }

  /**
   * Detect and log device changes (added/removed)
   * Groups devices by groupId to show physical devices with multiple capabilities together
   */
  private detectDeviceChanges(currentDevices: MediaDeviceInfo[]): void {
    if (this.previousDevices.length === 0) {
      // First check, no comparison possible
      return
    }

    const previousDeviceIds = new Set(this.previousDevices.map(d => d.deviceId))
    const currentDeviceIds = new Set(currentDevices.map(d => d.deviceId))

    // Find removed devices
    const removedDevices = this.previousDevices.filter(d => !currentDeviceIds.has(d.deviceId))
    
    // Find added devices
    const addedDevices = currentDevices.filter(d => !previousDeviceIds.has(d.deviceId))

    if (removedDevices.length > 0 || addedDevices.length > 0) {
      // Build structured device change information
      const deviceChangeInfo: {
        removed?: Array<{
          label: string
          capabilities: Array<{
            type: string
            deviceId: string
            groupId: string
          }>
        }>
        added?: Array<{
          label: string
          capabilities: Array<{
            type: string
            deviceId: string
            groupId: string
          }>
        }>
      } = {}
      
      if (removedDevices.length > 0) {
        // Group removed devices by groupId (physical device)
        const groupedRemoved = this.groupDevicesByGroupId(removedDevices)
        deviceChangeInfo.removed = Object.entries(groupedRemoved).map(([groupId, devices]) => {
          const deviceLabel = devices[0].label || '(label unavailable - permissions required)'
          return {
            label: deviceLabel,
            capabilities: devices.map(device => ({
              type: this.getDeviceTypeLabel(device.kind),
              deviceId: device.deviceId,
              groupId: groupId
            }))
          }
        })
      }
      
      if (addedDevices.length > 0) {
        // Group added devices by groupId (physical device)
        const groupedAdded = this.groupDevicesByGroupId(addedDevices)
        deviceChangeInfo.added = Object.entries(groupedAdded).map(([groupId, devices]) => {
          const deviceLabel = devices[0].label || '(label unavailable - permissions required)'
          return {
            label: deviceLabel,
            capabilities: devices.map(device => ({
              type: this.getDeviceTypeLabel(device.kind),
              deviceId: device.deviceId,
              groupId: groupId
            }))
          }
        })
      }
      
      // Create a hash of this device change to prevent duplicate logs
      const changeHash = JSON.stringify({
        removed: deviceChangeInfo.removed?.map(d => d.label).sort().join('|') || '',
        added: deviceChangeInfo.added?.map(d => d.label).sort().join('|') || ''
      })
      
      const now = Date.now()
      const isDuplicate = changeHash === this.lastDeviceChangeHash && 
                         (now - this.lastDeviceChangeLogTime) < this.DEVICE_CHANGE_LOG_COOLDOWN_MS
      
      if (isDuplicate) {
        // Skip logging duplicate change
        return
      }
      
      // Update tracking
      this.lastDeviceChangeHash = changeHash
      this.lastDeviceChangeLogTime = now
      
      // Log everything in a single structured message
      if (deviceChangeInfo.removed && deviceChangeInfo.removed.length > 0) {
        const deviceNames = deviceChangeInfo.removed.map(d => d.label).join(', ')
        logWithSuffix('warn', 'DEVICE-UNPLUGGED', `${deviceChangeInfo.removed.length} physical device(s) UNPLUGGED/REMOVED: ${deviceNames}`, deviceChangeInfo.removed)
      }
      
      if (deviceChangeInfo.added && deviceChangeInfo.added.length > 0) {
        const deviceNames = deviceChangeInfo.added.map(d => d.label).join(', ')
        logWithSuffix('warn', 'DEVICE-PLUGGED-IN', `${deviceChangeInfo.added.length} physical device(s) PLUGGED IN/ADDED: ${deviceNames}`, deviceChangeInfo.added)
      }
    }
  }

  /**
   * Group devices by their groupId (devices with same groupId are part of same physical device)
   */
  private groupDevicesByGroupId(devices: MediaDeviceInfo[]): Record<string, MediaDeviceInfo[]> {
    const grouped: Record<string, MediaDeviceInfo[]> = {}
    
    for (const device of devices) {
      const groupId = device.groupId
      if (!grouped[groupId]) {
        grouped[groupId] = []
      }
      grouped[groupId].push(device)
    }
    
    return grouped
  }

  /**
   * Get human-readable device type label
   */
  private getDeviceTypeLabel(kind: string): string {
    switch (kind) {
      case 'videoinput':
        return 'Camera'
      case 'audioinput':
        return 'Microphone'
      case 'audiooutput':
        return 'Audio Output'
      default:
        return kind
    }
  }

  /**
   * Setup device change listener to detect when devices are plugged/unplugged
   */
  private async setupDeviceChangeListener(): Promise<void> {
    if (!navigator.mediaDevices || !navigator.mediaDevices.addEventListener) {
      console.warn('Device change listener not supported in this browser')
      return
    }

    this.deviceChangeHandler = () => {
      // Debounce device change events to batch multiple rapid changes together
      // This handles cases where a single physical device has multiple capabilities
      // (e.g., Jabra with mic + audio output fires 2 events, but should be shown as 1 device)
      if (this.deviceChangeDebounceTimer) {
        clearTimeout(this.deviceChangeDebounceTimer)
      }
      
      this.deviceChangeDebounceTimer = window.setTimeout(async () => {
        // Use quiet mode to suppress verbose logging during device change events
        // Only the essential device change information will be logged
        try {
          await this.checkDeviceAvailability(true) // quietMode = true
          // Don't call checkPermissions() here to avoid cascading logs
          // The periodic check will handle permission state updates
        } catch (error) {
          console.warn('Error checking devices after change:', error)
        } finally {
          this.deviceChangeDebounceTimer = null
        }
      }, this.DEVICE_CHANGE_DEBOUNCE_MS)
    }

    navigator.mediaDevices.addEventListener('devicechange', this.deviceChangeHandler)
    logWithSuffix('debug', 'DEVICE-LISTENER-REGISTERED', 'Device change listener registered - will detect device plug/unplug events')
  }


  /**
   * Check current media permissions without requesting them
   * @param quietMode - If true, suppresses verbose device lists but still logs minimal heartbeat (useful during periodic checks)
   */
  async checkPermissions(quietMode: boolean = false): Promise<MediaPermissionState> {
    try {
      
      // Only log heartbeat in non-quiet mode (initial checks, manual checks)
      // In quiet mode (periodic checks), we only log if something is wrong
      // No URL suffix for routine checks - info is in payload
      if (!quietMode) {
        console.debug('Checking current media permissions...')
      }
      
      // Check device availability first (quiet mode passed through)
      const deviceAvailability = await this.checkDeviceAvailability(quietMode)
      
      // Check if permissions API is available
      if (!navigator.permissions) {
        logWithSuffix('warn', 'PERMISSION-API-UNAVAILABLE', 'Permissions API not available, falling back to getUserMedia test')
        return await this.checkPermissionsViaGetUserMedia(deviceAvailability)
      }

      const [cameraPermission, microphonePermission] = await Promise.all([
        navigator.permissions.query({ name: 'camera' as PermissionName }).catch(() => ({ state: 'unknown' as PermissionState })),
        navigator.permissions.query({ name: 'microphone' as PermissionName }).catch(() => ({ state: 'unknown' as PermissionState }))
      ])

      const newState = {
        camera: cameraPermission.state,
        microphone: microphonePermission.state,
        hasPermissions: cameraPermission.state === 'granted' && microphonePermission.state === 'granted',
        lastChecked: Date.now(),
        deviceAvailability
      }

      // Only update if state has changed
      const stateChanged = this.permissionState.camera !== newState.camera || 
          this.permissionState.microphone !== newState.microphone ||
          !this.permissionState.deviceAvailability ||
          this.permissionState.deviceAvailability.hasCamera !== newState.deviceAvailability.hasCamera ||
          this.permissionState.deviceAvailability.hasMicrophone !== newState.deviceAvailability.hasMicrophone

      if (stateChanged) {
        // State changed - log at INFO level (important event) with URL suffix for filtering
        logWithSuffix('info', 'PERMISSION-STATE-CHANGED', 'Permission state changed:', this.permissionState, '->', newState)
        this.permissionState = newState
        this.savePermissionState()
        this.notifyListeners()
      } else {
        // State unchanged - still refresh snapshot (reload checks must return current API state)
        this.permissionState = { ...newState, lastChecked: Date.now() }
        if (!quietMode) {
          const status = newState.hasPermissions ? 'granted' : (newState.camera === 'denied' || newState.microphone === 'denied' ? 'denied' : 'pending')
          console.debug(`Permission check completed - ${status} (camera: ${newState.camera}, mic: ${newState.microphone}, devices: ${newState.deviceAvailability.hasCamera ? 'camera' : 'no-camera'}/${newState.deviceAvailability.hasMicrophone ? 'mic' : 'no-mic'})`)
        }
      }

      return this.permissionState

    } catch (error) {
      logWithSuffix('warn', 'PERMISSION-CHECK-ERROR', 'Error checking permissions, falling back to getUserMedia test:', error)
      
      // Try fallback method
      try {
        const fallbackState = await this.checkPermissionsViaGetUserMedia()
        // Only log recovery in non-quiet mode - errors are already logged above
        if (!quietMode) {
          const status = fallbackState.hasPermissions ? 'granted' : (fallbackState.camera === 'denied' || fallbackState.microphone === 'denied' ? 'denied' : 'unknown')
          // No URL suffix for recovery - error was already logged with suffix
          console.debug(`Permission check completed after error recovery - ${status} (camera: ${fallbackState.camera}, mic: ${fallbackState.microphone})`)
        }
        return fallbackState
      } catch (fallbackError) {
        // Log complete failure - this is a problem, so use URL suffix
        logWithSuffix('warn', 'PERMISSION-CHECK-FAILED', `Permission check failed completely - unable to determine state (error: ${fallbackError})`)
        // Return last known state or default
        return this.permissionState
      }
    }
  }

  /**
   * Fallback method to check permissions by attempting to get media stream
   */
  private async checkPermissionsViaGetUserMedia(deviceAvailability?: { hasCamera: boolean, hasMicrophone: boolean }): Promise<MediaPermissionState> {
    try {
      // No URL suffix for routine fallback test - only log if it fails
      // console.debug('Testing permissions via getUserMedia...') // Removed - too verbose
      
      // Try to get a very short test stream
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1, height: 1 },
        audio: true
      })

      // If we get here, permissions are granted
      const newState = {
        camera: 'granted' as PermissionState,
        microphone: 'granted' as PermissionState,
        hasPermissions: true,
        lastChecked: Date.now(),
        deviceAvailability: deviceAvailability || await this.checkDeviceAvailability()
      }

      // Only update if state has changed
      if (this.permissionState.camera !== newState.camera || 
          this.permissionState.microphone !== newState.microphone) {
        logWithSuffix('info', 'PERMISSION-STATE-CHANGED', 'Permission state changed via getUserMedia:', this.permissionState, '->', newState)
        this.permissionState = newState
        this.savePermissionState()
        this.notifyListeners()
      }

      // Stop the test stream immediately
      stream.getTracks().forEach(track => track.stop())
      
      logWithSuffix('debug', 'PERMISSION-GRANTED-GETUSERMEDIA', 'Permissions granted via getUserMedia test')
      return this.permissionState

    } catch (error: any) {
      logWithSuffix('warn', 'PERMISSION-DENIED', 'Permissions denied or error:', error)
      
      // Use the enhanced error parsing function
      const errorParse = this.parseGetUserMediaError(error)
      const cameraState = errorParse.camera
      const microphoneState = errorParse.microphone
      const errorMessage = errorParse.specificError || error.message

      const newState = {
        camera: cameraState,
        microphone: microphoneState,
        hasPermissions: false,
        error: errorMessage,
        lastChecked: Date.now(),
        deviceAvailability: deviceAvailability || await this.checkDeviceAvailability()
      }

      // Only update if state has changed
      if (this.permissionState.camera !== newState.camera || 
          this.permissionState.microphone !== newState.microphone) {
        logWithSuffix('info', 'PERMISSION-DENIED-STATE', 'Permission state changed to denied:', this.permissionState, '->', newState)
        this.permissionState = newState
        this.savePermissionState()
        this.notifyListeners()
      }

      return this.permissionState
    }
  }

  /**
   * Debounced recheck when browser permission state changes (camera + mic fire separately).
   */
  private schedulePermissionRecheck(): void {
    if (this.permissionRecheckTimer !== null) {
      clearTimeout(this.permissionRecheckTimer)
    }
    this.permissionRecheckTimer = window.setTimeout(() => {
      this.permissionRecheckTimer = null
      void this.checkPermissions(true)
    }, this.PERMISSION_RECHECK_DEBOUNCE_MS)
  }

  /**
   * Request media permissions via getUserMedia (user gesture only — e.g. MediaPermissionPrompt).
   * Skips getUserMedia if Permissions API already reports granted.
   */
  async requestPermissions(): Promise<MediaStreamInfo> {
    try {
      if (navigator.permissions) {
        const [cameraPermission, microphonePermission] = await Promise.all([
          navigator.permissions.query({ name: 'camera' as PermissionName }).catch(() => null),
          navigator.permissions.query({ name: 'microphone' as PermissionName }).catch(() => null),
        ])
        if (
          cameraPermission?.state === 'granted' &&
          microphonePermission?.state === 'granted'
        ) {
          this.permissionState = {
            camera: 'granted',
            microphone: 'granted',
            hasPermissions: true,
            lastChecked: Date.now(),
            deviceAvailability:
              this.permissionState.deviceAvailability ||
              (await this.checkDeviceAvailability(true)),
          }
          this.savePermissionState()
          this.notifyListeners()
          logWithSuffix('debug', 'PERMISSION-ALREADY-GRANTED', 'Skipping getUserMedia — already granted')
          return { video: true, audio: true }
        }
      } else if (this.permissionState.hasPermissions) {
        return { video: true, audio: true }
      }

      logWithSuffix('debug', 'PERMISSION-REQUEST', 'Requesting media permissions via getUserMedia...')
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      })

      const videoTracks = stream.getVideoTracks()
      const audioTracks = stream.getAudioTracks()

      const result: MediaStreamInfo = {
        video: videoTracks.length > 0,
        audio: audioTracks.length > 0,
      }

      // Release camera/mic immediately — Unblu owns capture during calls
      stopMediaStream(stream)

      // Update permission state
      this.permissionState = {
        camera: videoTracks.length > 0 ? 'granted' : 'denied',
        microphone: audioTracks.length > 0 ? 'granted' : 'denied',
        hasPermissions: result.video && result.audio
      }

      logWithSuffix('info', 'PERMISSION-GRANTED', 'Media permissions granted:', result)
      this.savePermissionState()
      this.notifyListeners()
      return result

    } catch (error: any) {
      logWithSuffix('error', 'MEDIA-PERMISSION-FAILED', 'Failed to get media permissions:', error)
      
      const result: MediaStreamInfo = {
        video: false,
        audio: false,
        error: error.message
      }

      // Update permission state
      this.permissionState = {
        camera: 'denied',
        microphone: 'denied',
        hasPermissions: false,
        error: error.message
      }

      return result
    }
  }

  /**
   * Test media devices without keeping the stream
   */
  async testMediaDevices(): Promise<MediaStreamInfo> {
    try {
      // This is our "I'm alive" check - always log (even in quiet mode)
      // No URL suffix - just regular logging
      console.debug('Testing media devices...')
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      })

      const videoTracks = stream.getVideoTracks()
      const audioTracks = stream.getAudioTracks()

      const result: MediaStreamInfo = {
        video: videoTracks.length > 0,
        audio: audioTracks.length > 0,
        stream: stream
      }

      // Stop the test stream immediately
      stream.getTracks().forEach(track => track.stop())
      
      // Always log success (even in quiet mode) - no URL suffix
      console.debug('Media devices test successful:', { video: result.video, audio: result.audio })
      return result

    } catch (error: any) {
      // Always log failure as warning (even in quiet mode - this is a problem) - no URL suffix
      console.warn('Media devices test failed:', error)
      
      return {
        video: false,
        audio: false,
        error: error.message
      }
    }
  }

  /**
   * Get current permission state
   */
  getPermissionState(): MediaPermissionState {
    return { ...this.permissionState }
  }

  /**
   * Check if we have the required permissions for video calls
   */
  hasRequiredPermissions(): boolean {
    return this.permissionState.hasPermissions
  }

  /**
   * Get user-friendly error message
   */
  getErrorMessage(): string {
    if (!this.permissionState.error) {
      return 'Media permissions are required for video calls'
    }

    const error = this.permissionState.error.toLowerCase()
    
    if (error.includes('notallowed') || error.includes('permission denied')) {
      return 'Camera and microphone access was denied. Please allow access and refresh the page.'
    }
    
    if (error.includes('notfound') || error.includes('no device')) {
      return 'No camera or microphone found. Please connect a device and try again.'
    }
    
    if (error.includes('notreadable') || error.includes('in use')) {
      return 'Camera or microphone is already in use by another application.'
    }
    
    if (error.includes('notsupported') || error.includes('not implemented')) {
      return 'Your browser does not support video calls. Please use a modern browser.'
    }
    
    return 'Unable to access camera and microphone. Please check your browser settings.'
  }

  /**
   * Reset permission state
   */
  reset(): void {
    this.permissionState = {
      camera: 'unknown',
      microphone: 'unknown',
      hasPermissions: false
    }
    // Clear previous devices to avoid false change detection after reset
    this.previousDevices = []
  }

  /**
   * Check if we're in a secure context (required for getUserMedia)
   */
  isSecureContext(): boolean {
    return window.isSecureContext
  }

  /**
   * Get browser compatibility info
   */
  getBrowserInfo(): { 
    hasGetUserMedia: boolean
    hasPermissionsAPI: boolean
    isSecureContext: boolean
    userAgent: string
  } {
    return {
      hasGetUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
      hasPermissionsAPI: !!navigator.permissions,
      isSecureContext: window.isSecureContext,
      userAgent: navigator.userAgent
    }
  }

  /**
   * Enhanced error parsing for more specific error detection
   */
  private parseGetUserMediaError(error: any): { camera: PermissionState, microphone: PermissionState, specificError?: string } {
    const errorName = error.name?.toLowerCase() || ''
    const errorMessage = error.message?.toLowerCase() || ''
    
    // More specific error parsing
    if (errorName === 'notallowederror') {
      return { camera: 'denied', microphone: 'denied', specificError: 'Permission denied by user' }
    }
    
    if (errorName === 'notfounderror') {
      return { camera: 'denied', microphone: 'denied', specificError: 'No media devices found' }
    }
    
    if (errorName === 'notreadableerror') {
      return { camera: 'denied', microphone: 'denied', specificError: 'Device is in use by another application' }
    }
    
    if (errorName === 'overconstrainederror') {
      return { camera: 'denied', microphone: 'denied', specificError: 'Device constraints cannot be satisfied' }
    }
    
    if (errorName === 'securityerror') {
      return { camera: 'denied', microphone: 'denied', specificError: 'Security error - HTTPS required' }
    }
    
    if (errorName === 'aborterror') {
      return { camera: 'denied', microphone: 'denied', specificError: 'Request was aborted' }
    }
    
    return { camera: 'denied', microphone: 'denied', specificError: errorMessage || 'Unknown error' }
  }

  /**
   * Retry logic with exponential backoff
   */
  private async retryWithBackoff<T>(
    fn: () => Promise<T>, 
    maxRetries: number = 3, 
    baseDelay: number = 1000
  ): Promise<T> {
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await fn()
      } catch (error) {
        if (i === maxRetries - 1) throw error
        const delay = baseDelay * Math.pow(2, i)
        console.log(`Retry ${i + 1}/${maxRetries} in ${delay}ms...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
    throw new Error('Max retries exceeded')
  }

  /**
   * Save permission state to localStorage
   */
  private async savePermissionState(): Promise<void> {
    try {
      const stateToSave = {
        camera: this.permissionState.camera,
        microphone: this.permissionState.microphone,
        hasPermissions: this.permissionState.hasPermissions,
        lastChecked: this.permissionState.lastChecked,
        deviceAvailability: this.permissionState.deviceAvailability
      }
      localStorage.setItem('mediaPermissions', JSON.stringify(stateToSave))
      logWithSuffix('debug', 'PERMISSION-STATE-SAVED', 'Permission state saved to localStorage')
    } catch (e) {
      logWithSuffix('warn', 'PERMISSION-STATE-SAVE-ERROR', 'Could not save permission state to localStorage:', e)
    }
  }

  /**
   * Load permission state from localStorage
   */
  private async loadPermissionState(): Promise<void> {
    try {
      const saved = localStorage.getItem('mediaPermissions')
      if (saved) {
        const parsedState = JSON.parse(saved)
        // Only load if the saved state is recent (within 24 hours)
        const isRecent = parsedState.lastChecked && (Date.now() - parsedState.lastChecked) < 24 * 60 * 60 * 1000
        if (isRecent) {
          this.permissionState = { ...this.permissionState, ...parsedState }
          logWithSuffix('debug', 'PERMISSION-STATE-LOADED', 'Permission state loaded from localStorage:', this.permissionState)
        } else {
          logWithSuffix('debug', 'PERMISSION-STATE-EXPIRED', 'Saved permission state is too old, ignoring')
        }
      }
    } catch (e) {
      logWithSuffix('warn', 'PERMISSION-STATE-LOAD-ERROR', 'Could not load permission state from localStorage:', e)
    }
  }

  /**
   * Add permission change listener
   */
  addPermissionListener(callback: () => void): void {
    this.permissionListeners.push(callback)
    this.setupPermissionListeners().catch(() => {
      // Silently fail - permission listener setup
    })
  }

  /**
   * Remove permission change listener
   */
  removePermissionListener(callback: () => void): void {
    this.permissionListeners = this.permissionListeners.filter(cb => cb !== callback)
  }

  /**
   * Setup permission change listeners
   */
  private async setupPermissionListeners(): Promise<void> {
    if (this.isListening || !navigator.permissions) return
    
    this.isListening = true
    logWithSuffix('debug', 'PERMISSION-LISTENERS-SETUP', 'Setting up permission change listeners...')
    
    // Listen for camera permission changes
    navigator.permissions.query({ name: 'camera' as PermissionName })
      .then(result => {
        result.onchange = () => {
          logWithSuffix('info', 'PERMISSION-CAMERA-CHANGED', 'Camera permission changed:', result.state)
          this.schedulePermissionRecheck()
        }
      })
      .catch(async err => {
        logWithSuffix('warn', 'PERMISSION-LISTENER-SETUP-ERROR', 'Could not set up camera permission listener:', err)
      })
    
    // Listen for microphone permission changes
    navigator.permissions.query({ name: 'microphone' as PermissionName })
      .then(result => {
        result.onchange = () => {
          logWithSuffix('info', 'PERMISSION-MICROPHONE-CHANGED', 'Microphone permission changed:', result.state)
          this.schedulePermissionRecheck()
        }
      })
      .catch(async err => {
        logWithSuffix('warn', 'PERMISSION-LISTENER-SETUP-ERROR', 'Could not set up microphone permission listener:', err)
      })
  }

  /**
   * Notify all listeners of permission state changes
   */
  private notifyListeners(): void {
    for (const callback of this.permissionListeners) {
      try {
        callback()
      } catch (error) {
        logWithSuffix('warn', 'PERMISSION-LISTENER-ERROR', 'Error in permission listener:', error)
      }
    }
  }

  /**
   * Enhanced request permissions with retry logic
   */
  async requestPermissionsWithRetry(): Promise<MediaStreamInfo> {
    return this.retryWithBackoff(() => this.requestPermissions(), 3, 1000)
  }
}

// Export singleton instance
export const mediaPermissionManager = MediaPermissionManager.getInstance()

// Export convenience functions
export const checkMediaPermissions = () => mediaPermissionManager.checkPermissions()
export const requestMediaPermissions = () => mediaPermissionManager.requestPermissions()
export const requestMediaPermissionsWithRetry = () => mediaPermissionManager.requestPermissionsWithRetry()
export const testMediaDevices = () => mediaPermissionManager.testMediaDevices()
export const hasRequiredPermissions = () => mediaPermissionManager.hasRequiredPermissions()
export const getMediaErrorMessage = () => mediaPermissionManager.getErrorMessage()
export const checkDeviceAvailability = () => mediaPermissionManager.checkDeviceAvailability()
export const addPermissionListener = (callback: () => void) => mediaPermissionManager.addPermissionListener(callback)
export const removePermissionListener = (callback: () => void) => mediaPermissionManager.removePermissionListener(callback)
